/**
 * Spaces Query Helpers
 *
 * Shared TanStack Query keys and options for client-side Spaces data.
 *
 * Primary responsibilities:
 * - Define stable query keys for Spaces surfaces
 * - Provide typed query options for list, detail, members, and content
 * - Centralize cache invalidation for Space-related mutations
 *
 * Dependencies:
 * - @tanstack/react-query
 * - db/spaces
 * - db/space-members
 * - lib/spaces/hierarchy-loader
 */

import {
  queryOptions,
  type QueryClient
} from '@tanstack/react-query'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import { getSpaceActivities } from '@/db/space-activity'
import {
  getSpaceMemberDirectory,
  getSpaceMembers,
  type SpaceMemberQueryOptions
} from '@/db/space-members'
import {
  getSpacesByWorkspaceId,
  getSpaceById,
  isSpaceAuthRequestError,
  SpaceRequestError
} from '@/db/spaces'
import {
  fetchSpaceItemDetail,
  type SpaceItemDetailTarget
} from '@/lib/spaces/item-details'
import {
  getSpaceWhiteboard,
  getSpaceWhiteboards
} from '@/db/space-whiteboards'
import { loadSpaceHierarchy, clearSpaceHierarchyCache } from '@/lib/spaces/hierarchy-loader'
import type { SpaceTaskVisibilityScope } from '@/lib/spaces/task-visibility'
import type {
  SpaceMemberDirectory,
  SpaceSelectableItemType
} from '@/types/spaces'
import type { Tables } from '@/supabase/types'

const SPACES_STALE_TIME_MS = 30_000
// Sidebar expand used to race an 8s client timeout against cold serverless
// hierarchy loads (even empty spaces). Prefer a longer budget + auto-retries.
const SPACES_QUERY_TIMEOUT_MS =
  process.env.NODE_ENV === 'development' ? null : 25_000
const SPACES_QUERY_MAX_RETRIES = 2
const SPACE_DETAIL_STALE_TIME_MS = 5_000
const SPACE_ITEM_DETAIL_STALE_TIME_MS = 10_000
const SPACES_GC_TIME_MS = 5 * 60 * 1000
export const SPACES_QUERY_TIMEOUT_ERROR_NAME = 'SpacesQueryTimeoutError'

type SpaceDesignQueryRecord = Tables<'space_designs'> & {
  href?: string | null
}

type SpaceDesignListPayload =
  | { designs?: SpaceDesignQueryRecord[] }
  | SpaceDesignQueryRecord[]

type SpaceDesignDetailPayload =
  | ({ design?: SpaceDesignQueryRecord } & Partial<SpaceDesignQueryRecord>)
  | SpaceDesignQueryRecord

const createSpacesTimeoutError = (label: string) => {
  const error = new Error(`${label} timed out`)
  error.name = SPACES_QUERY_TIMEOUT_ERROR_NAME
  return error
}

const isSpacesQueryTimeoutError = (error: unknown) =>
  error instanceof Error && error.name === SPACES_QUERY_TIMEOUT_ERROR_NAME

const withSpacesQueryTimeout = async <T>(
  promise: Promise<T>,
  label: string,
  abortController?: AbortController
): Promise<T> => {
  if (!SPACES_QUERY_TIMEOUT_MS) {
    return promise
  }

  let timeoutId: ReturnType<typeof setTimeout> | null = null

  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          abortController?.abort()
          reject(createSpacesTimeoutError(label))
        }, SPACES_QUERY_TIMEOUT_MS)
      })
    ])
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  }
}

const isAbortError = (error: unknown) =>
  (error instanceof DOMException && error.name === 'AbortError') ||
  (error instanceof Error && error.name === 'AbortError')

const isRetryableSpaceStatus = (status: number) =>
  status === 408 || status === 425 || status === 429 || status >= 500

const isRetryableSpacesQueryError = (error: unknown) => {
  if (isAbortError(error) || isSpaceAuthRequestError(error)) {
    return false
  }

  if (error instanceof SpaceRequestError) {
    return isRetryableSpaceStatus(error.status)
  }

  // Timeouts, network failures, and other transient errors.
  return true
}

const shouldRetrySpacesQuery = (failureCount: number, error: unknown) => {
  return isRetryableSpacesQueryError(error) && failureCount < SPACES_QUERY_MAX_RETRIES
}

const spacesQueryRetryDelay = (attemptIndex: number, error: unknown) => {
  // attemptIndex is 0-based for the first retry.
  const baseMs = isSpacesQueryTimeoutError(error) ? 1_500 : 750
  const exponentialMs = Math.min(baseMs * 2 ** attemptIndex, 8_000)
  const jitterMs = Math.floor(Math.random() * 300)
  return exponentialMs + jitterMs
}

const normalizeMemberQueryOptions = (
  options: SpaceMemberQueryOptions = {}
) => ({
  query: options.query?.trim() || '',
  limit: typeof options.limit === 'number' ? options.limit : null
})

const normalizeSpaceDesign = (
  payload: SpaceDesignDetailPayload | null
): SpaceDesignQueryRecord | null => {
  if (!payload) {
    return null
  }

  if ('design' in payload && payload.design) {
    return payload.design
  }

  return 'id' in payload ? payload : null
}

const fetchSpaceDesigns = async (
  spaceId: string
): Promise<SpaceDesignQueryRecord[]> => {
  const response = await authenticatedFetch(`/api/spaces/${spaceId}/designs`, {
    cache: 'no-store'
  })

  if (!response.ok) {
    throw new Error('Failed to load designs')
  }

  const payload = (await response.json()) as SpaceDesignListPayload
  return Array.isArray(payload) ? payload : payload.designs ?? []
}

const fetchSpaceDesignDetail = async (
  spaceId: string,
  designId: string
): Promise<SpaceDesignQueryRecord | null> => {
  const response = await authenticatedFetch(`/api/spaces/${spaceId}/designs/${designId}`, {
    cache: 'no-store'
  })

  if (response.ok) {
    const payload = (await response.json()) as SpaceDesignDetailPayload
    return normalizeSpaceDesign(payload)
  }

  if (response.status !== 404) {
    throw new Error('Failed to load design')
  }

  const designs = await fetchSpaceDesigns(spaceId)
  return designs.find(design => design.id === designId) ?? null
}

export const spacesQueryKeys = {
  all: ['spaces'] as const,
  list: (workspaceId: string) =>
    [...spacesQueryKeys.all, 'list', workspaceId] as const,
  detail: (spaceId: string) =>
    [...spacesQueryKeys.all, 'detail', spaceId] as const,
  members: (spaceId: string) =>
    [...spacesQueryKeys.all, 'members', spaceId] as const,
  memberSearch: (spaceId: string, query: string, limit: number | null) =>
    [...spacesQueryKeys.members(spaceId), query || 'all', limit ?? 'default'] as const,
  memberDirectory: (spaceId: string) =>
    [...spacesQueryKeys.all, 'member-directory', spaceId] as const,
  repoLinks: (spaceId: string) =>
    [...spacesQueryKeys.all, 'repo-links', spaceId] as const,
  memberDirectorySearch: (
    spaceId: string,
    query: string,
    limit: number | null
  ) =>
    [
      ...spacesQueryKeys.memberDirectory(spaceId),
      query || 'all',
      limit ?? 'default'
    ] as const,
  hierarchy: (
    spaceId: string,
    showArchived: boolean,
    taskScope: SpaceTaskVisibilityScope = 'me'
  ) =>
    [
      ...spacesQueryKeys.all,
      'hierarchy',
      spaceId,
      showArchived ? 'archived' : 'active',
      taskScope
    ] as const,
  activities: (spaceId: string, limit: number) =>
    [...spacesQueryKeys.all, 'activities', spaceId, limit] as const,
  designs: (spaceId: string) =>
    [...spacesQueryKeys.all, 'designs', spaceId] as const,
  designDetail: (spaceId: string, designId: string) =>
    [...spacesQueryKeys.all, 'design-detail', spaceId, designId] as const,
  whiteboards: (spaceId: string) =>
    [...spacesQueryKeys.all, 'whiteboards', spaceId] as const,
  whiteboardDetail: (spaceId: string, whiteboardId: string) =>
    [...spacesQueryKeys.all, 'whiteboard-detail', spaceId, whiteboardId] as const,
  itemDetail: (
    spaceId: string,
    itemType: SpaceSelectableItemType,
    itemId: string
  ) =>
    [...spacesQueryKeys.all, 'item-detail', spaceId, itemType, itemId] as const
}

const defaultTiming = {
  staleTime: SPACES_STALE_TIME_MS,
  gcTime: SPACES_GC_TIME_MS,
  refetchOnReconnect: true,
  refetchOnWindowFocus: true,
  retry: shouldRetrySpacesQuery,
  retryDelay: spacesQueryRetryDelay
}

export const spacesListQueryOptions = (workspaceId: string) =>
  queryOptions({
    queryKey: spacesQueryKeys.list(workspaceId),
    queryFn: () =>
      withSpacesQueryTimeout(
        getSpacesByWorkspaceId(workspaceId),
        'Spaces list query'
      ),
    ...defaultTiming
  })

export const spaceDetailQueryOptions = (spaceId: string) =>
  queryOptions({
    queryKey: spacesQueryKeys.detail(spaceId),
    queryFn: () => getSpaceById(spaceId),
    retry: shouldRetrySpacesQuery,
    retryDelay: spacesQueryRetryDelay,
    gcTime: SPACES_GC_TIME_MS,
    staleTime: SPACE_DETAIL_STALE_TIME_MS,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true
  })

export const spaceMembersQueryOptions = (
  spaceId: string,
  options: SpaceMemberQueryOptions = {}
) => {
  const normalizedOptions = normalizeMemberQueryOptions(options)

  return queryOptions({
    queryKey: spacesQueryKeys.memberSearch(
      spaceId,
      normalizedOptions.query,
      normalizedOptions.limit
    ),
    queryFn: () => getSpaceMembers(spaceId, normalizedOptions),
    ...defaultTiming
  })
}

export const spaceMemberDirectoryQueryOptions = (
  spaceId: string,
  options: SpaceMemberQueryOptions = {}
) => {
  const normalizedOptions = normalizeMemberQueryOptions(options)

  return queryOptions<SpaceMemberDirectory>({
    queryKey: spacesQueryKeys.memberDirectorySearch(
      spaceId,
      normalizedOptions.query,
      normalizedOptions.limit
    ),
    queryFn: () => getSpaceMemberDirectory(spaceId, normalizedOptions),
    ...defaultTiming
  })
}

export const spaceHierarchyQueryOptions = (
  spaceId: string,
  showArchived: boolean,
  taskScope: SpaceTaskVisibilityScope = 'me'
) =>
  queryOptions({
    queryKey: spacesQueryKeys.hierarchy(spaceId, showArchived, taskScope),
    queryFn: async ({ signal }) => {
      const timeoutController = new AbortController()
      const onAbort = () => timeoutController.abort()
      signal.addEventListener('abort', onAbort)

      try {
        const loadPromise = loadSpaceHierarchy(spaceId, {
          showArchived,
          taskScope,
          forceRefresh: false,
          signal: timeoutController.signal
        })
        // Prevent unhandled rejection when timeout wins the race and aborts.
        void loadPromise.catch(() => undefined)

        return await withSpacesQueryTimeout(
          loadPromise,
          'Space hierarchy query',
          timeoutController
        )
      } catch (error) {
        // Drop any aborted/timed-out inflight so the next auto-retry is cold.
        if (isSpacesQueryTimeoutError(error) || isAbortError(error)) {
          clearSpaceHierarchyCache(spaceId)
        }
        throw error
      } finally {
        signal.removeEventListener('abort', onAbort)
      }
    },
    ...defaultTiming
  })

export const spaceActivitiesQueryOptions = (
  spaceId: string,
  limit: number = 10
) =>
  queryOptions({
    queryKey: spacesQueryKeys.activities(spaceId, limit),
    queryFn: () =>
      withSpacesQueryTimeout(
        getSpaceActivities(spaceId, limit),
        "Space activities query"
      ),
    ...defaultTiming
  })

export const spaceDesignsQueryOptions = (spaceId: string) =>
  queryOptions({
    queryKey: spacesQueryKeys.designs(spaceId),
    queryFn: () => fetchSpaceDesigns(spaceId),
    ...defaultTiming
  })

export const spaceDesignDetailQueryOptions = (
  spaceId: string,
  designId: string
) =>
  queryOptions({
    queryKey: spacesQueryKeys.designDetail(spaceId, designId),
    queryFn: async () => {
      const data = await fetchSpaceDesignDetail(spaceId, designId)
      return data?.space_id === spaceId ? data : null
    },
    retry: shouldRetrySpacesQuery,
    retryDelay: spacesQueryRetryDelay,
    gcTime: SPACES_GC_TIME_MS,
    staleTime: SPACE_DETAIL_STALE_TIME_MS,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true
  })

export const spaceWhiteboardsQueryOptions = (spaceId: string) =>
  queryOptions({
    queryKey: spacesQueryKeys.whiteboards(spaceId),
    queryFn: () =>
      withSpacesQueryTimeout(
        getSpaceWhiteboards(spaceId),
        "Space whiteboards query"
      ),
    ...defaultTiming
  })

export const spaceWhiteboardDetailQueryOptions = (
  spaceId: string,
  whiteboardId: string
) =>
  queryOptions({
    queryKey: spacesQueryKeys.whiteboardDetail(spaceId, whiteboardId),
    queryFn: () =>
      withSpacesQueryTimeout(
        getSpaceWhiteboard(spaceId, whiteboardId),
        "Space whiteboard detail query"
      ),
    retry: shouldRetrySpacesQuery,
    retryDelay: spacesQueryRetryDelay,
    gcTime: SPACES_GC_TIME_MS,
    staleTime: SPACE_DETAIL_STALE_TIME_MS,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true
  })

export const spaceItemDetailQueryOptions = <
  T extends SpaceSelectableItemType
>(
  spaceId: string,
  item: SpaceItemDetailTarget<T>
) =>
  queryOptions({
    queryKey: spacesQueryKeys.itemDetail(spaceId, item.type, item.id),
    queryFn: () =>
      withSpacesQueryTimeout(
        fetchSpaceItemDetail(spaceId, item),
        "Space item detail query"
      ),
    retry: shouldRetrySpacesQuery,
    retryDelay: spacesQueryRetryDelay,
    gcTime: SPACES_GC_TIME_MS,
    staleTime: SPACE_ITEM_DETAIL_STALE_TIME_MS,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true
  })

interface InvalidateSpaceQueriesOptions {
  workspaceId?: string | null
  spaceId?: string | null
}

export const invalidateSpaceQueries = async (
  queryClient: QueryClient,
  options: InvalidateSpaceQueriesOptions
) => {
  const invalidations: Promise<unknown>[] = []

  if (options.workspaceId) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: spacesQueryKeys.list(options.workspaceId)
      })
    )
  }

  if (options.spaceId) {
    clearSpaceHierarchyCache(options.spaceId)

    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: spacesQueryKeys.detail(options.spaceId)
      })
    )
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: spacesQueryKeys.members(options.spaceId)
      })
    )
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: spacesQueryKeys.memberDirectory(options.spaceId)
      })
    )
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: [...spacesQueryKeys.all, 'hierarchy', options.spaceId]
      })
    )
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: [...spacesQueryKeys.all, 'activities', options.spaceId]
      })
    )
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: spacesQueryKeys.whiteboards(options.spaceId)
      })
    )
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: [...spacesQueryKeys.all, 'item-detail', options.spaceId]
      })
    )
  }

  if (invalidations.length === 0) {
    return
  }

  await Promise.all(invalidations)
}
